import React, { useState, useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { getAuthHeaders } from '../../utils/auth';
import NotificationDialog from '../../components/NotificationDialog';
import UserAvatar from '../../components/UserAvatar';
import AvatarColorPicker from '../../components/AvatarColorPicker';
import { resolveAvatarColor } from '../../utils/userAvatar';

const ProfileSection = () => {
  const { user, refreshUser } = useAuth();
  const displayName = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || 'Account';

  const [avatarColor, setAvatarColor] = useState(user?.avatarColor || resolveAvatarColor());
  const [avatarSaving, setAvatarSaving] = useState(false);
  const [avatarError, setAvatarError] = useState('');
  const [avatarSuccessOpen, setAvatarSuccessOpen] = useState(false);

  useEffect(() => {
    setAvatarColor(user?.avatarColor || resolveAvatarColor());
  }, [user?.avatarColor]);

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccessOpen, setPasswordSuccessOpen] = useState(false);

  const handleAvatarSave = async () => {
    setAvatarError('');
    try {
      setAvatarSaving(true);
      await axios.patch(
        '/api/auth/me/avatar-color',
        { avatarColor },
        { headers: getAuthHeaders() }
      );
      await refreshUser?.();
      setAvatarSuccessOpen(true);
    } catch (error) {
      setAvatarError(error.response?.data?.error || 'Failed to save avatar color');
    } finally {
      setAvatarSaving(false);
    }
  };

  const handlePasswordFieldChange = (event) => {
    const { name, value } = event.target;
    setPasswordForm((prev) => ({ ...prev, [name]: value }));
    setPasswordError('');
  };

  const handlePasswordSubmit = async (event) => {
    event.preventDefault();
    setPasswordError('');

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters long');
      return;
    }

    try {
      setPasswordSaving(true);
      await axios.post(
        '/api/auth/change-password',
        {
          currentPassword: passwordForm.currentPassword,
          newPassword: passwordForm.newPassword,
        },
        { headers: getAuthHeaders() }
      );
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setPasswordSuccessOpen(true);
    } catch (error) {
      setPasswordError(error.response?.data?.error || 'Failed to change password');
    } finally {
      setPasswordSaving(false);
    }
  };

  return (
    <Stack spacing={3}>
      <Card>
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <UserAvatar
              firstName={user?.firstName}
              lastName={user?.lastName}
              email={user?.email}
              avatarColor={avatarColor}
              size={56}
              sx={{ fontSize: '1.25rem' }}
            />
            <Box>
              <Typography variant="h6">{displayName}</Typography>
              <Typography variant="body2" color="text.secondary">
                {user?.email}
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Avatar color
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose a color for your initials icon.
          </Typography>
          {avatarError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {avatarError}
            </Alert>
          )}
          <AvatarColorPicker
            value={avatarColor}
            onChange={setAvatarColor}
            onSave={handleAvatarSave}
            saving={avatarSaving}
          />
        </CardContent>
      </Card>

      <Card>
        <CardContent component="form" onSubmit={handlePasswordSubmit}>
          <Typography variant="h6" gutterBottom>
            Change Password
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter your current password, then choose a new password.
          </Typography>

          <Divider sx={{ mb: 3 }} />

          {passwordError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {passwordError}
            </Alert>
          )}

          <Stack spacing={2} sx={{ maxWidth: 420 }}>
            <TextField
              fullWidth
              label="Current password"
              name="currentPassword"
              type={showCurrentPassword ? 'text' : 'password'}
              value={passwordForm.currentPassword}
              onChange={handlePasswordFieldChange}
              required
              autoComplete="current-password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle current password visibility"
                      onClick={() => setShowCurrentPassword((v) => !v)}
                      edge="end"
                    >
                      {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="New password"
              name="newPassword"
              type={showNewPassword ? 'text' : 'password'}
              value={passwordForm.newPassword}
              onChange={handlePasswordFieldChange}
              required
              autoComplete="new-password"
              helperText="At least 6 characters"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle new password visibility"
                      onClick={() => setShowNewPassword((v) => !v)}
                      edge="end"
                    >
                      {showNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="Confirm new password"
              name="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              value={passwordForm.confirmPassword}
              onChange={handlePasswordFieldChange}
              required
              autoComplete="new-password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle confirm password visibility"
                      onClick={() => setShowConfirmPassword((v) => !v)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Box>
              <Button type="submit" variant="contained" disabled={passwordSaving}>
                {passwordSaving ? 'Updating…' : 'Update password'}
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <NotificationDialog
        open={passwordSuccessOpen}
        onClose={() => setPasswordSuccessOpen(false)}
        title="Password updated"
        message="Your password has been changed successfully."
        iconPreset="info"
        buttons={['dismiss']}
        buttonLabels={{ dismiss: 'OK' }}
      />

      <NotificationDialog
        open={avatarSuccessOpen}
        onClose={() => setAvatarSuccessOpen(false)}
        title="Avatar color saved"
        message="Your icon color has been updated."
        iconPreset="info"
        buttons={['dismiss']}
        buttonLabels={{ dismiss: 'OK' }}
      />
    </Stack>
  );
};

export default ProfileSection;
